import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, Mail, Webhook, Globe, Zap } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const TriggerNodeConfig = ({ node, onUpdate }) => {
  const [triggerType, setTriggerType] = useState(
    node.data?.config?.triggerType || "manual"
  );
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(
    node.data?.config?.connectionId || ""
  );
  const [config, setConfig] = useState(node.data?.config || {});

  useEffect(() => {
    fetchConnections();
  }, [triggerType]);

  const fetchConnections = async () => {
    try {
      // Fetch connections based on trigger type
      let connectionType = null;
      if (triggerType === "email") connectionType = "email";
      else if (triggerType === "webhook") connectionType = "webhook";
      else if (triggerType === "http") connectionType = "http_api";

      if (connectionType) {
        const response = await axios.get(
          `${API}/connections?connection_type=${connectionType}`,
          { withCredentials: true }
        );
        setConnections(response.data.connections || []);
      }
    } catch (error) {
      console.error("Failed to fetch connections:", error);
    }
  };

  const handleTriggerTypeChange = (type) => {
    setTriggerType(type);
    setSelectedConnection("");
    setConfig({ ...config, triggerType: type });
    updateNode({ ...config, triggerType: type, connectionId: "" });
  };

  const handleConnectionChange = (connectionId) => {
    setSelectedConnection(connectionId);
    const newConfig = { ...config, connectionId };
    setConfig(newConfig);
    updateNode(newConfig);
  };

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    updateNode(newConfig);
  };

  const updateNode = (newConfig) => {
    onUpdate({
      ...node,
      data: {
        ...node.data,
        label: getTriggerLabel(newConfig.triggerType),
        config: newConfig,
      },
    });
  };

  const getTriggerLabel = (type) => {
    const labels = {
      manual: "Manual Trigger",
      schedule: "Schedule",
      email: "Email Received",
      webhook: "Webhook",
      http: "HTTP Request",
    };
    return labels[type] || "Start";
  };

  const getTriggerIcon = (type) => {
    switch (type) {
      case "schedule":
        return <Clock className="w-4 h-4" />;
      case "email":
        return <Mail className="w-4 h-4" />;
      case "webhook":
        return <Webhook className="w-4 h-4" />;
      case "http":
        return <Globe className="w-4 h-4" />;
      default:
        return <Zap className="w-4 h-4" />;
    }
  };

  const renderTriggerConfig = () => {
    switch (triggerType) {
      case "schedule":
        return (
          <div className="space-y-4">
            <div>
              <Label>Schedule Type</Label>
              <Select
                value={config.scheduleType || "cron"}
                onValueChange={(value) => handleConfigChange("scheduleType", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cron">Cron Expression</SelectItem>
                  <SelectItem value="interval">Interval</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.scheduleType === "cron" && (
              <div>
                <Label>Cron Expression</Label>
                <Input
                  value={config.cronExpression || ""}
                  onChange={(e) => handleConfigChange("cronExpression", e.target.value)}
                  placeholder="0 9 * * *"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Example: "0 9 * * *" runs daily at 9:00 AM
                </p>
              </div>
            )}

            {config.scheduleType === "interval" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Interval</Label>
                  <Input
                    type="number"
                    value={config.intervalValue || 1}
                    onChange={(e) =>
                      handleConfigChange("intervalValue", parseInt(e.target.value))
                    }
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select
                    value={config.intervalUnit || "hours"}
                    onValueChange={(value) => handleConfigChange("intervalUnit", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {config.scheduleType === "daily" && (
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={config.dailyTime || "09:00"}
                  onChange={(e) => handleConfigChange("dailyTime", e.target.value)}
                />
              </div>
            )}

            {config.scheduleType === "weekly" && (
              <div>
                <Label>Day of Week</Label>
                <Select
                  value={config.weeklyDay || "monday"}
                  onValueChange={(value) => handleConfigChange("weeklyDay", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monday">Monday</SelectItem>
                    <SelectItem value="tuesday">Tuesday</SelectItem>
                    <SelectItem value="wednesday">Wednesday</SelectItem>
                    <SelectItem value="thursday">Thursday</SelectItem>
                    <SelectItem value="friday">Friday</SelectItem>
                    <SelectItem value="saturday">Saturday</SelectItem>
                    <SelectItem value="sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        );

      case "email":
      case "webhook":
      case "http":
        return (
          <div className="space-y-4">
            <div>
              <Label>Connection</Label>
              {connections.length > 0 ? (
                <Select
                  value={selectedConnection}
                  onValueChange={handleConnectionChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a connection" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map((conn) => (
                      <SelectItem key={conn.connection_id} value={conn.connection_id}>
                        {conn.connection_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No connections found.{" "}
                  <Button
                    variant="link"
                    className="p-0 h-auto"
                    onClick={() => window.open("/connections", "_blank")}
                  >
                    Create one
                  </Button>
                </div>
              )}
            </div>

            {triggerType === "email" && (
              <>
                <div>
                  <Label>Filter</Label>
                  <Select
                    value={config.emailFilter || "all"}
                    onValueChange={(value) => handleConfigChange("emailFilter", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Emails</SelectItem>
                      <SelectItem value="unread">Unread Only</SelectItem>
                      <SelectItem value="from">From Specific Sender</SelectItem>
                      <SelectItem value="subject">Subject Contains</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {config.emailFilter === "from" && (
                  <div>
                    <Label>From Email</Label>
                    <Input
                      value={config.fromEmail || ""}
                      onChange={(e) => handleConfigChange("fromEmail", e.target.value)}
                      placeholder="sender@example.com"
                    />
                  </div>
                )}

                {config.emailFilter === "subject" && (
                  <div>
                    <Label>Subject Contains</Label>
                    <Input
                      value={config.subjectContains || ""}
                      onChange={(e) =>
                        handleConfigChange("subjectContains", e.target.value)
                      }
                      placeholder="invoice"
                    />
                  </div>
                )}
              </>
            )}

            {triggerType === "webhook" && selectedConnection && (
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-sm font-medium">Webhook URL</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    value={`${API}/webhooks/trigger/${node.id}`}
                    readOnly
                    className="font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `${API}/webhooks/trigger/${node.id}`
                      );
                      toast.success("Webhook URL copied!");
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Send POST requests to this URL to trigger the workflow
                </p>
              </div>
            )}
          </div>
        );

      case "manual":
      default:
        return (
          <div className="text-sm text-muted-foreground">
            This workflow will be triggered manually from the dashboard or via API.
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-base font-semibold">Trigger Type</Label>
        <p className="text-sm text-muted-foreground mb-3">
          Choose how this workflow should be triggered
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: "manual", label: "Manual", icon: <Zap className="w-4 h-4" /> },
            { value: "schedule", label: "Schedule", icon: <Clock className="w-4 h-4" /> },
            { value: "email", label: "Email", icon: <Mail className="w-4 h-4" /> },
            { value: "webhook", label: "Webhook", icon: <Webhook className="w-4 h-4" /> },
            { value: "http", label: "HTTP", icon: <Globe className="w-4 h-4" /> },
          ].map((trigger) => (
            <Button
              key={trigger.value}
              variant={triggerType === trigger.value ? "default" : "outline"}
              className="justify-start"
              onClick={() => handleTriggerTypeChange(trigger.value)}
            >
              {trigger.icon}
              <span className="ml-2">{trigger.label}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="border-t pt-6">{renderTriggerConfig()}</div>

      {selectedConnection && (
        <div className="flex items-center gap-2">
          <Badge variant="success" className="bg-green-500/10 text-green-600">
            Connected
          </Badge>
          <span className="text-xs text-muted-foreground">
            Using connection from vault
          </span>
        </div>
      )}
    </div>
  );
};

export default TriggerNodeConfig;
